import type { TFunction } from 'i18next';
import { ChartLine, Filter, LayoutGrid, Medal, PieChart, type LucideIcon } from 'lucide-react';
import type { ComponentType } from 'react';
import { tileOf } from '../overview-tiles.ts';
import { BreakdownWidget } from './BreakdownWidget.tsx';
import { FunnelWidget } from './FunnelWidget.tsx';
import { KpiWidget } from './KpiWidget.tsx';
import { SETTINGS_FIELDS, type SettingsFieldsProps } from './settings-fields.tsx';
import { TopProductsWidget } from './TopProductsWidget.tsx';
import { TrendWidget } from './TrendWidget.tsx';
import type { WidgetProps } from './widget-props.ts';
import type { DashboardWidget, WidgetKind, WidgetOfKind } from './widget-types.ts';

export type WidgetDefinition<K extends WidgetKind> = {
  readonly icon: LucideIcon;
  readonly framed: boolean;
  readonly titleOf: (widget: WidgetOfKind<K>, t: TFunction) => string;
  readonly needsFinance: (widget: WidgetOfKind<K>) => boolean;
  readonly Render: ComponentType<WidgetProps<K>>;
  readonly Fields: ComponentType<SettingsFieldsProps<K>>;
};

const always = (): boolean => true;

export const WIDGET_DEFINITIONS: { readonly [K in WidgetKind]: WidgetDefinition<K> } = {
  kpi: {
    icon: LayoutGrid,
    framed: false,
    titleOf: (widget, t) => t(`dashboard.overview.${widget.settings.tile}`),
    needsFinance: (widget) => tileOf(widget.settings.tile).source === 'report',
    Render: KpiWidget,
    Fields: SETTINGS_FIELDS.kpi,
  },
  trend: {
    icon: ChartLine,
    framed: true,
    titleOf: (widget, t) => t(`dashboard.widgets.trendTitle.${widget.settings.measure}`),
    needsFinance: always,
    Render: TrendWidget,
    Fields: SETTINGS_FIELDS.trend,
  },
  breakdown: {
    icon: PieChart,
    framed: true,
    titleOf: (widget, t) =>
      t(`dashboard.widgets.breakdownTitle.${widget.settings.measure}`, {
        dimension: t(`dashboard.widgets.dimension.${widget.settings.dimension}`),
      }),
    needsFinance: always,
    Render: BreakdownWidget,
    Fields: SETTINGS_FIELDS.breakdown,
  },
  funnel: {
    icon: Filter,
    framed: true,
    titleOf: (_widget, t) => t('dashboard.widgets.kind.funnel'),
    needsFinance: always,
    Render: FunnelWidget,
    Fields: SETTINGS_FIELDS.funnel,
  },
  topProducts: {
    icon: Medal,
    framed: true,
    titleOf: (_widget, t) => t('dashboard.overview.topProductsTitle'),
    needsFinance: always,
    Render: TopProductsWidget,
    Fields: SETTINGS_FIELDS.topProducts,
  },
};

export const definitionOf = (widget: DashboardWidget): WidgetDefinition<WidgetKind> =>
  WIDGET_DEFINITIONS[widget.kind] as unknown as WidgetDefinition<WidgetKind>;

export const widgetTitleOf = (widget: DashboardWidget, t: TFunction): string =>
  widget.title ?? definitionOf(widget).titleOf(widget, t);

export type WidgetViewer = { readonly financeVisible: boolean; readonly productFiltered: boolean };

const isShopWide = (widget: DashboardWidget): boolean =>
  widget.kind === 'kpi' && tileOf(widget.settings.tile).shopWide === true;

export const isWidgetVisible = (widget: DashboardWidget, viewer: WidgetViewer): boolean =>
  (viewer.financeVisible || !definitionOf(widget).needsFinance(widget)) &&
  !(viewer.productFiltered && isShopWide(widget));
